/**
 * Rapprocher le relevé bancaire de la grille prévisionnelle.
 *
 * La grille annonce « Fournitures : 12 000 € en octobre ». Le relevé, lui, dit
 * « VIR SEPA POINT-P MANOSQUE 1 240,00 ». Les deux parlent du même argent sans
 * jamais se rencontrer : rien, dans un mouvement bancaire, ne porte le nom d'un
 * poste. C'est ce chaînon que ce module fabrique, au moyen de règles écrites
 * une fois : « tout libellé contenant POINT P va dans Fournitures ».
 *
 * Trois principes, et ils expliquent presque tout le code :
 *
 *  1. **Un mouvement compte une fois et une seule.** Les règles sont essayées
 *     dans l'ordre, la première qui prend l'emporte. Sans cela le même euro se
 *     retrouverait dans deux postes, et les totaux mentiraient sans que rien ne
 *     le signale.
 *  2. **Ce qui n'est pas classé n'est pas perdu.** Un mouvement qu'aucune règle
 *     ne reconnaît ressort dans une liste à part, avec son montant. Un
 *     classement silencieusement incomplet serait pire qu'un classement absent.
 *  3. **Une règle de sortie déduit les entrées qu'elle reconnaît.** Un avoir du
 *     fournisseur porte le même libellé que ses factures : il doit diminuer le
 *     poste, pas s'y ajouter.
 */

export type SensRegle = 'debit' | 'credit';

export interface RegleClassement {
  id: string;
  /** Le poste de la grille prévisionnelle qui reçoit le mouvement. */
  posteId: string;
  /**
   * Un ou plusieurs fragments cherchés dans le libellé, sans accent ni casse.
   * La règle prend dès que L'UN d'eux figure dans le libellé : le même
   * fournisseur s'écrit « POINT P », « POINT-P » et « POINTP » selon le jour.
   */
  motifs: readonly string[];
  /** `debit` pour un poste de charge, `credit` pour un poste d'encaissement. */
  sens: SensRegle;
  /** Les règles sont essayées dans cet ordre croissant. */
  ordre: number;
  /**
   * Bornes de montant, en valeur absolue. `null` des deux côtés : la règle ne
   * regarde que le libellé. Trois prélèvements URSSAF du même libellé à
   * 1 695 €, 1 547 € et 420 € se séparent par là, et pas autrement.
   */
  montantMin: number | null;
  montantMax: number | null;
}

export interface MouvementAClasser {
  id: string;
  /** `AAAA-MM-JJ`. */
  jour: string;
  libelle: string;
  debit: number;
  credit: number;
  /**
   * Poste imposé à la main, par son nom.
   *
   * Il l'emporte sur toutes les règles, sans exception : un chèque émis ou un
   * effet domicilié ne porte qu'un numéro, aucune règle ne le reconnaîtra
   * jamais, et c'est le seul moyen de le ranger. Une décision prise à la main
   * ne se fait pas déloger par un automatisme.
   */
  posteImpose: string | null;
}

/**
 * La forme sous laquelle on compare un libellé de relevé.
 *
 * Les libellés bancaires sont écrits en capitales, ponctués d'étoiles, de
 * barres obliques et de numéros de mandat. « POINT-P » et « POINT P » désignent
 * le même fournisseur : tout ce qui n'est ni lettre ni chiffre devient une
 * espace, et les accents tombent.
 */
export function normaliserLibelle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// ----- La règle par défaut ---------------------------------------------------
//
// Un relevé compte des centaines d'encaissements clients : virements au nom de
// chaque client, remises de chèques numérotées. Une règle par client serait
// sans fin, et une remise de chèques ne nomme personne. D'où une règle qui ne
// regarde pas le libellé : « toute entrée qu'aucune autre règle ne reconnaît
// va dans Encaissements clients ».
//
// Elle s'écrit avec un motif fait d'étoiles. Aucune colonne nouvelle : comme
// les virgules entre deux orthographes, c'est une convention de lecture. Une
// version du CRM qui ne la connaît pas réduit « ** » à un motif vide, qui ne
// reconnaît rien : la règle y est inerte, jamais dangereuse.
//
// Trois garde-fous :
//  - elle passe APRÈS toutes les autres, quel que soit son rang : une règle
//    écrite pour un tiers précis l'emporte toujours ;
//  - elle ne prend que les mouvements de SON sens : une règle d'entrées par
//    défaut ne ramasse aucune sortie (elle la compterait en négatif) ;
//  - ses bornes de montant, s'il y en a, s'appliquent comme aux autres.

/** Le motif stocké pour une règle par défaut (la base exige deux caractères). */
export const MOTIF_PAR_DEFAUT = '**';

/** Ce fragment est-il le joker « tout libellé » ? */
export function estMotifParDefaut(motif: string): boolean {
  return /^\*+$/.test(motif.trim());
}

/** Une règle par défaut ne regarde pas le libellé : tous ses motifs sont des jokers. */
export function estRegleParDefaut(r: Pick<RegleClassement, 'motifs'>): boolean {
  return r.motifs.length > 0 && r.motifs.every(estMotifParDefaut);
}

/**
 * L'ordre dans lequel les règles sont essayées : les règles par défaut en
 * dernier, puis le rang, puis l'identifiant (pour qu'un ex æquo ne dépende pas
 * du hasard de la lecture).
 */
export function comparerOrdre(a: RegleClassement, b: RegleClassement): number {
  return (
    Number(estRegleParDefaut(a)) - Number(estRegleParDefaut(b)) ||
    a.ordre - b.ordre ||
    a.id.localeCompare(b.id)
  );
}

/** Ce que le mouvement porte, quel que soit son sens. Sert aux bornes de montant. */
function montantAbsolu(m: MouvementAClasser): number {
  return Math.max(Math.abs(m.debit), Math.abs(m.credit));
}

/** Cette règle reconnaît-elle ce mouvement ? */
export function regleReconnait(m: MouvementAClasser, r: RegleClassement): boolean {
  const montant = montantAbsolu(m);
  if (r.montantMin !== null && montant < r.montantMin) return false;
  if (r.montantMax !== null && montant > r.montantMax) return false;

  // La règle par défaut prend tout libellé — mais seulement dans son sens.
  if (estRegleParDefaut(r)) return montantRetenu(m, r.sens) > 0;

  const libelle = normaliserLibelle(m.libelle);
  return r.motifs.some((brut) => {
    const motif = normaliserLibelle(brut);
    // Un motif vide reconnaîtrait tout : il ne reconnaît rien.
    return motif !== '' && libelle.includes(motif);
  });
}

/** La première règle qui reconnaît ce mouvement, ou `null`. */
export function regleQuiPrend(
  mouvement: MouvementAClasser,
  regles: readonly RegleClassement[],
): RegleClassement | null {
  const ordonnees = [...regles].sort(comparerOrdre);
  return ordonnees.find((r) => regleReconnait(mouvement, r)) ?? null;
}

/**
 * Ce que ce mouvement apporte au poste.
 *
 * Une règle de sortie compte le débit et retranche le crédit : l'avoir du
 * fournisseur porte le même libellé que ses factures, et doit diminuer le
 * poste. Une règle d'entrée fait l'inverse.
 */
export function montantRetenu(mouvement: MouvementAClasser, sens: SensRegle): number {
  const debit = Number.isFinite(mouvement.debit) ? mouvement.debit : 0;
  const credit = Number.isFinite(mouvement.credit) ? mouvement.credit : 0;
  return sens === 'debit' ? debit - credit : credit - debit;
}

export interface Classement {
  /** Pour chaque poste, douze montants réalisés (index 0 = janvier). */
  parPoste: Map<string, number[]>;
  /** Pour chaque mouvement classé, le poste qui l'a reçu. */
  posteParMouvement: Map<string, string>;
  /** Pour chaque mouvement classé par une règle, laquelle. Vide s'il est imposé. */
  regleParMouvement: Map<string, string>;
  /** Ce qu'aucune règle n'a reconnu, dans l'ordre d'origine. */
  nonClasses: MouvementAClasser[];
}

function moisDe(jour: string): number | null {
  const m = /^\d{4}-(\d{2})-\d{2}/.exec(jour);
  if (!m) return null;
  const i = Number(m[1]) - 1;
  return i >= 0 && i < 12 ? i : null;
}

/** Range les mouvements d'une année dans les postes, mois par mois. */
export function classer(
  mouvements: readonly MouvementAClasser[],
  regles: readonly RegleClassement[],
  /** Le nom de poste → son identifiant, pour résoudre un classement à la main. */
  posteParNom: ReadonlyMap<string, string> = new Map(),
  /**
   * Le sens de chaque poste, pour un mouvement rangé à la main.
   *
   * Sans lui, un mouvement rangé à la main compte toujours en positif : un
   * chèque impayé (une sortie) rangé dans « Encaissements clients » GONFLAIT
   * les encaissements au lieu de les diminuer. Avec lui, il compte comme une
   * règle du même sens l'aurait compté. Un poste absent de la table garde
   * l'ancien comportement.
   */
  sensParPoste: ReadonlyMap<string, SensRegle> = new Map(),
): Classement {
  const parPoste = new Map<string, number[]>();
  const posteParMouvement = new Map<string, string>();
  const regleParMouvement = new Map<string, string>();
  const nonClasses: MouvementAClasser[] = [];

  const ajouter = (posteId: string, mois: number, montant: number): void => {
    const douze = parPoste.get(posteId) ?? Array.from({ length: 12 }, () => 0);
    douze[mois] = (douze[mois] ?? 0) + montant;
    parPoste.set(posteId, douze);
  };

  for (const m of mouvements) {
    const mois = moisDe(m.jour);
    if (mois === null) {
      nonClasses.push(m);
      continue;
    }

    // Une décision prise à la main ne se fait pas déloger par un automatisme.
    if (m.posteImpose !== null && m.posteImpose !== '') {
      const posteId = posteParNom.get(m.posteImpose);
      if (posteId === undefined) {
        // Le poste a disparu de la grille de l'année : le mouvement ressort,
        // plutôt que d'alimenter un poste qui n'existe plus.
        nonClasses.push(m);
        continue;
      }
      const sens = sensParPoste.get(posteId);
      ajouter(
        posteId,
        mois,
        sens === undefined ? (m.debit > 0 ? m.debit : m.credit) : montantRetenu(m, sens),
      );
      posteParMouvement.set(m.id, posteId);
      continue;
    }

    const regle = regleQuiPrend(m, regles);
    if (regle === null) {
      nonClasses.push(m);
      continue;
    }
    ajouter(regle.posteId, mois, montantRetenu(m, regle.sens));
    posteParMouvement.set(m.id, regle.posteId);
    regleParMouvement.set(m.id, regle.id);
  }

  // Arrondi au centime une seule fois, à la fin : additionner des montants
  // déjà arrondis fait dériver le total.
  for (const [posteId, douze] of parPoste) {
    parPoste.set(
      posteId,
      douze.map((v) => Math.round(v * 100) / 100),
    );
  }

  return { parPoste, posteParMouvement, regleParMouvement, nonClasses };
}

/**
 * Ce qu'une règle prendrait — ou a pris — sur ce relevé.
 *
 * Sert deux fois : AVANT d'enregistrer une règle, pour montrer ce qu'elle va
 * faire, et APRÈS, pour montrer ce qu'elle a fait. Une règle qu'on applique
 * sans voir ce qu'elle ramasse se découvre un mois plus tard, dans un total
 * faux.
 *
 * `autresRegles` sont celles qui passent AVANT : un mouvement qu'elles ont
 * déjà pris n'est pas compté ici, puisque la première l'emporte.
 */
export interface ApercuRegle {
  pris: MouvementAClasser[];
  sorties: number;
  entrees: number;
  /** Mouvements que cette règle reconnaît mais qu'une règle antérieure a déjà pris. */
  dejaPris: MouvementAClasser[];
}

export function apercuRegle(
  regle: RegleClassement,
  mouvements: readonly MouvementAClasser[],
  autresRegles: readonly RegleClassement[] = [],
): ApercuRegle {
  // Celles qui passent avant elle, dans l'ordre même du classement — une règle
  // par défaut passe après toutes les autres, quel que soit son rang.
  const avant = autresRegles.filter((r) => r.id !== regle.id && comparerOrdre(r, regle) < 0);
  const pris: MouvementAClasser[] = [];
  const dejaPris: MouvementAClasser[] = [];
  let sorties = 0;
  let entrees = 0;

  for (const m of mouvements) {
    // Un mouvement rangé à la main n'appartient à aucune règle.
    if (m.posteImpose !== null && m.posteImpose !== '') continue;
    if (!regleReconnait(m, regle)) continue;
    if (regleQuiPrend(m, avant) !== null) {
      dejaPris.push(m);
      continue;
    }
    pris.push(m);
    sorties += Math.max(0, m.debit);
    entrees += Math.max(0, m.credit);
  }

  return {
    pris,
    sorties: Math.round(sorties * 100) / 100,
    entrees: Math.round(entrees * 100) / 100,
    dejaPris,
  };
}

/**
 * L'écart entre ce qui était prévu et ce qui est sorti.
 *
 * `null` quand rien n'était prévu ET que rien n'est sorti : il n'y a alors pas
 * d'écart à montrer, juste une case vide. Un prévu absent avec du réalisé est
 * en revanche un écart entier — c'est une dépense que personne n'avait vue venir.
 */
export function ecart(prevu: number | null, realise: number): number | null {
  if (prevu === null && realise === 0) return null;
  return Math.round((realise - (prevu ?? 0)) * 100) / 100;
}

// ----- Proposer des règles --------------------------------------------------

/**
 * Ce que les banques écrivent AVANT le nom du tiers.
 *
 * Un relevé français ne dit pas « URSSAF » : il dit « Prélèvement - URSSAF
 * PROVENCE ALPES », « Virement émis - VIR INST vers DUPONT », « Virement reçu
 * en votre faveur de SCI LA FARE ». Garder ces préfixes faisait proposer
 * « emis web », « en votre » ou « faveur inst » — des bouts de phrase qui ne
 * désignent personne et qu'on retrouve sur la moitié du relevé.
 *
 * On les retire par la GAUCHE, dans l'ordre, et tant qu'il en reste : un
 * libellé en cumule souvent deux ou trois.
 */
const PREFIXES_BANQUE: readonly RegExp[] = [
  /^virements?\b/,
  /^(?:emis|emise|emises|recu|recue|recus|permanents?)\b/,
  /^(?:vir|vrst)\b/,
  /^inst(?:antane)?\b/,
  /^(?:en )?(?:votre|sa|leur) faveur\b/,
  /^(?:vers|de la part de|de|par|pour)\b/,
  /^prelevements?\b/,
  /^(?:prlv|plv)\b/,
  /^sepa\b/,
  /^(?:paiement|achat|retrait)s? (?:par )?(?:carte|cb)\b/,
  /^(?:carte|cb)\b/,
  /^(?:cheques?|chq)\b/,
  /^remises? (?:de )?(?:cheques?|chq)?\b/,
  /^effets? (?:domicilies?|domicilie)?\b/,
  /^(?:web|internet|en ligne)\b/,
  /^(?:frais|commission|cotisation)s?\b/,
  /^(?:echeance|ech)\b/,
  /^(?:mandat|rum|ref|reference|num|numero|no|n)\b/,
  /^(?:du|le|la|les|des|de|et|a|au|aux)\b/,
  /^\d+\b/,
];

/**
 * Le nom du tiers, dégagé de ce que la banque a écrit autour.
 *
 * Rend une chaîne vide quand il ne reste rien d'exploitable — un chèque émis ou
 * un effet domicilié ne porte qu'un numéro, et aucune règle ne pourra le
 * reconnaître. Ces mouvements-là se rangent à la main.
 */
export function nomDuTiers(libelle: string): string {
  let reste = normaliserLibelle(libelle);
  let encore = true;
  while (encore) {
    encore = false;
    for (const prefixe of PREFIXES_BANQUE) {
      const court = reste.replace(prefixe, '').trim();
      if (court !== reste) {
        reste = court;
        encore = true;
        break;
      }
    }
  }
  // Ce qui suit un numéro de pièce n'apporte plus rien : « URSSAF PACA 102026 »
  // et « URSSAF PACA 112026 » désignent le même tiers.
  return reste
    .split(' ')
    .filter((mot) => mot !== '' && !/^\d+$/.test(mot) && !MOTS_RESIDUELS.has(mot))
    .join(' ')
    .trim();
}

/** Ce qui traîne encore après les préfixes, et ne désigne personne. */
const MOTS_RESIDUELS = new Set([
  'sarl',
  'sas',
  'sa',
  'eurl',
  'sasu',
  'sci',
  'ste',
  'societe',
  'monsieur',
  'madame',
  'mr',
  'mme',
  'm',
  'facture',
  'fact',
  'solde',
  'acompte',
  'remboursement',
]);

export interface MotifPropose {
  /** Le fragment à mettre dans la règle. */
  motif: string;
  /** Combien de mouvements distincts il reconnaît. */
  occurrences: number;
  /** Ce qui SORT sur ces mouvements. */
  sorties: number;
  /** Ce qui ENTRE. Jamais additionné aux sorties : ce total ne voudrait rien dire. */
  entrees: number;
  /** Le sens à proposer, celui qui pèse le plus. */
  sens: SensRegle;
}

/** Au plus deux mots : un seul est trop large, trois ne se répètent jamais. */
function motifDe(tiers: string): string | null {
  const mots = tiers.split(' ').filter((m) => m.length >= 3);
  if (mots.length === 0) return null;
  return mots.slice(0, 2).join(' ');
}

/**
 * Ce qui revient assez souvent pour mériter une règle.
 *
 * Le motif proposé est le NOM DU TIERS, dégagé de ce que la banque écrit
 * autour — pas une suite de mots prise au hasard dans la phrase. Un mouvement
 * ne nourrit qu'une proposition : il ne peut pas apparaître sur deux lignes.
 */
export function suggererMotifs(
  nonClasses: readonly MouvementAClasser[],
  occurrencesMin = 2,
  maxResultats = 12,
): MotifPropose[] {
  const vus = new Map<string, { ids: Set<string>; sorties: number; entrees: number }>();

  for (const m of nonClasses) {
    const motif = motifDe(nomDuTiers(m.libelle));
    // Un chèque émis ou un effet domicilié ne porte qu'un numéro : aucune règle
    // ne le reconnaîtra, et proposer un motif vide serait pire que rien.
    if (motif === null) continue;
    const acc = vus.get(motif) ?? { ids: new Set<string>(), sorties: 0, entrees: 0 };
    if (!acc.ids.has(m.id)) {
      acc.ids.add(m.id);
      acc.sorties += Math.max(0, m.debit);
      acc.entrees += Math.max(0, m.credit);
    }
    vus.set(motif, acc);
  }

  const retenus = [...vus.entries()]
    .filter(([, v]) => v.ids.size >= occurrencesMin)
    .map(([motif, v]) => ({
      motif,
      occurrences: v.ids.size,
      sorties: Math.round(v.sorties * 100) / 100,
      entrees: Math.round(v.entrees * 100) / 100,
      sens: v.sorties >= v.entrees ? ('debit' as const) : ('credit' as const),
      ids: v.ids,
    }))
    .sort(
      (a, b) =>
        Math.max(b.sorties, b.entrees) - Math.max(a.sorties, a.entrees) ||
        b.occurrences - a.occurrences ||
        a.motif.localeCompare(b.motif),
    );

  // Une proposition dont les mouvements sont tous repris par une autre ne sert
  // à rien : la règle la plus large les aurait déjà pris, et la seconde ne
  // prendrait jamais. On ne garde que les propositions qui apportent du neuf.
  const couverts = new Set<string>();
  const uniques: MotifPropose[] = [];
  for (const r of retenus) {
    const neuf = [...r.ids].filter((id) => !couverts.has(id));
    if (neuf.length === 0) continue;
    for (const id of r.ids) couverts.add(id);
    uniques.push({
      motif: r.motif,
      occurrences: r.occurrences,
      sorties: r.sorties,
      entrees: r.entrees,
      sens: r.sens,
    });
    if (uniques.length >= maxResultats) break;
  }
  return uniques;
}
